import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { Loader2, ListTodo, RotateCcw, XCircle } from "lucide-react";

// ONE IDEA OF A TASK (item 16). One screen over three stores (scrum actions / board / Lens),
// one status vocabulary. The lapsed pile — the largest pile of stalled work in Rex — is shown
// as its own worked-through section with reopen/close, not hidden behind a flag.
const STATUS_STYLE = {
  open: "bg-blue-100 text-blue-700", in_progress: "bg-indigo-100 text-indigo-700",
  blocked: "bg-amber-100 text-amber-800", done: "bg-emerald-100 text-emerald-700",
  lapsed: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500",
};
const KIND_LABEL = { scrum_action: "Scrum", scrum_task: "Board", lens_task: "Lens" };

export default function Tasks() {
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState("open");
  const [error, setError] = useState(null);
  const reqSeq = React.useRef(0);

  const load = () => {
    const seq = ++reqSeq.current;
    setTasks(null); // clear stale rows immediately on filter change
    fetch(`${API}/tasks/summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))).then(setSummary).catch((e) => setError(e.message));
    const q = filter === "all" ? "?include_terminal=true&limit=2000" : `?status=${filter}&limit=2000`;
    fetch(`${API}/tasks${q}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (seq === reqSeq.current) setTasks(d.tasks); }) // ignore out-of-order responses
      .catch((e) => setError(e.message));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const statuses = ["open", "in_progress", "blocked", "lapsed", "done", "cancelled", "all"];
  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="tasks-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Tasks</h1>
        <p className="mt-1 text-sm text-slate-500">One view over scrum actions, the board and Lens. One set of states.</p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="tasks-error">{error}</div>}

      {summary && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-white p-5" data-testid="tasks-hero">
          <div className="flex items-end gap-6">
            <div>
              <div className="text-4xl font-bold text-red-600" data-testid="hero-lapsed">{summary.lapsed_pile}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">stalled (lapsed)</div>
            </div>
            <div className="pb-1 text-2xl text-slate-300">vs</div>
            <div>
              <div className="text-4xl font-bold text-slate-900" data-testid="hero-live">{summary.open_total}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-400">live work</div>
            </div>
            {summary.open_total > 0 && (
              <div className="ml-auto pb-1 text-right text-sm text-slate-500">
                The stalled pile is <b className="text-red-600">{(summary.lapsed_pile / summary.open_total).toFixed(1)}×</b> the live work.
              </div>
            )}
          </div>
        </div>
      )}

      {summary && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="tasks-summary">
          {Object.entries(summary.by_status).map(([s, n]) => (
            <span key={s} className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLE[s] || "bg-slate-100 text-slate-600"}`} data-testid={`summary-${s}`}>
              {s.replace("_", " ")}: {n}
            </span>
          ))}
        </div>
      )}

      {summary?.lapsed_pile > 0 && filter !== "lapsed" && (
        <button onClick={() => setFilter("lapsed")} data-testid="lapsed-pile-cta"
          className="mb-4 flex w-full items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-5 py-3.5 text-left hover:bg-red-100">
          <span className="flex-1 text-sm text-red-900"><b>{summary.lapsed_pile}</b> lapsed commitments — the biggest pile of stalled work. Work through them.</span>
          <span className="text-xs font-medium text-red-700">Open →</span>
        </button>
      )}

      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="tasks-filters">
        {statuses.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`rounded-full px-3 py-1 text-xs font-medium ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {!tasks && !error && <div className="flex items-center gap-2 py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}
      {tasks && tasks.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-slate-400" data-testid="tasks-empty">Nothing here.</div>
      )}

      {tasks && tasks.length > 0 && (
        <div className="mb-2 text-xs text-slate-400" data-testid="tasks-count">Showing {tasks.length} {filter === "all" ? "" : filter.replace("_", " ")} task{tasks.length !== 1 ? "s" : ""}</div>
      )}

      <div className="space-y-2">
        {tasks && tasks.map((t, i) => (
          <div key={`${t.kind}-${t.task_id}-${i}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="task-row">
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500">{KIND_LABEL[t.kind] || t.kind}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-900">{t.title || "(untitled)"}</div>
              <div className="truncate text-xs text-slate-500">
                {t.owner || "no owner"}{t.client_name ? ` · ${t.client_name}` : ""}{t.due_date ? ` · due ${String(t.due_date).slice(0, 10)}` : ""}
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[t.status] || ""}`} data-testid="task-status">{t.status.replace("_", " ")}</span>
            {t.status === "lapsed" && <LapsedActions actionId={t.task_id} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function LapsedActions({ actionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "reopen") {
        const due = window.prompt("New due date (YYYY-MM-DD) to reopen with:");
        if (due === null) { setBusy(false); return; } // cancelled — do nothing
        if (due && !/^\d{4}-\d{2}-\d{2}$/.test(due)) { alert("Please use YYYY-MM-DD"); setBusy(false); return; }
        await fetch(`${API}/tasks/lapsed/${actionId}/reopen`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ new_due_date: due || null }) });
      } else {
        const reason = window.prompt("Reason for closing this lapsed commitment:") || "";
        if (!reason.trim()) { setBusy(false); return; }
        await fetch(`${API}/tasks/lapsed/${actionId}/close`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }) });
      }
      onDone && onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="flex shrink-0 gap-1">
      <button onClick={() => act("reopen")} disabled={busy} title="Reopen with a new date" data-testid="lapsed-reopen"
        className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40"><RotateCcw className="h-4 w-4" /></button>
      <button onClick={() => act("close")} disabled={busy} title="Close with a reason" data-testid="lapsed-close"
        className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"><XCircle className="h-4 w-4" /></button>
    </div>
  );
}
